import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { formatShortDateTime } from '../utils/timeUtils';
import axios from 'axios';
import { AlertModal, ConfirmModal } from './AlertModal';
import { IconUser, IconUsers } from './Icons';
import Pagination from './Pagination';
import './PublicMatches.css';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import MatchListHltv, { Logo as TeamMark, MatchActionIcon } from './matches/MatchListHltv';
import './matches/matchCards.css';
import './matches/matchesPage.css';

function Matches() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPanelAdmin = user?.role === 'panel_admin';
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const userId = user?.id;
  const userSteam = user?.steam_id;
  const isSubAdmin = user?.is_subadmin === true;
  // For sub-admins, if data is returned by the backend, they are authorized to manage it
  const canManage = (m) => isSuperAdmin || isSubAdmin || (userSteam && m.created_by_steam && m.created_by_steam === userSteam) || (!userSteam && m.created_by && m.created_by === userId);

  const navigate = useNavigate();
  const location = useLocation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null); // preserved for RCON if needed, though mostly unused in list view
  const [showRconModal, setShowRconModal] = useState(false);
  const [rconCommand, setRconCommand] = useState('');
  const [rconResponse, setRconResponse] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMyOnly, setShowMyOnly] = useState(false); // default: All
  // 'hltv' (default — compact list grouped by day) | 'cards' — remembered in this browser
  const [viewMode, setViewModeState] = useState(() => { try { return localStorage.getItem('fluxbot_matches_view') === 'cards' ? 'cards' : 'hltv'; } catch (_) { return 'hltv'; } });
  const setViewMode = (v) => { setViewModeState(v); try { localStorage.setItem('fluxbot_matches_view', v); } catch (_) { } };
  const [fetchError, setFetchError] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const mountedRef = useRef(true);
  // Loading guards: one request at a time for the poll, only the newest answer is applied,
  // and the poll always calls the CURRENT fetchData (search / filter / page of this render)
  const inFlightRef = useRef(false);
  const seqRef = useRef(0);
  const loadedRef = useRef(false);
  const fetchRef = useRef(null);

  // Old links (?create=1[&season=<id>], bookmarks) → the Create Match page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1') {
      const season = params.get('season');
      navigate(`/admin/matches/new${season ? `?season=${encodeURIComponent(season)}` : ''}`, { replace: true });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFetchError(null);
    fetchData();
    mountedRef.current = true;

    function connect() {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = (host === 'localhost' || host === '127.0.0.1') ? ':5000' : (window.location.port ? `:${window.location.port}` : '');
      const token = localStorage.getItem('token');
      const wsUrl = `${protocol}//${host}${port}/ws${token ? `?token=${token}` : ''}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ [Matches] WebSocket Connected to:', wsUrl);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'score_update' && message.data) {
            console.log('⚡ [Matches] Score Update:', message.data);
            setMatches(prev => (prev || []).map(m => m.id === message.data.match_id ? { ...m, score_team1: message.data.score_team1, score_team2: message.data.score_team2 } : m));
          } else if (message.type === 'match_stage_change' && message.data) {
            console.log('⚡ [Matches] Stage Change:', message.data);
            setMatches(prev => (prev || []).map(m => m.id === message.data.match_id ? { ...m, match_stage: message.data.stage } : m));
          } else if (message.type === 'match_status_change' && message.data) {
            console.log('⚡ [Matches] Status Change:', message.data);
            setMatches(prev => (prev || []).map(m => m.id === message.data.match_id ? { ...m, status: message.data.status } : m));
          } else if (message.type === 'match_finished' || message.type === 'match_started') {
            console.log('⚡ [Matches] Match Finished/Started');
            fetchData();
          } else if (message.type === 'tech_pause' && message.data) {
            console.log('🔧 [Matches] Tech Pause:', message.data);
            window.dispatchEvent(new CustomEvent('techPause', { detail: message.data }));
          }
        } catch (error) {
          console.error('WS Parse Error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ [Matches] WS Error:', error);
      };

      ws.onclose = (event) => {
        console.warn('⚠️ [Matches] WS Closed:', event.code, event.reason);
        wsRef.current = null;
        if (mountedRef.current) reconnectRef.current = setTimeout(connect, 3000);
      };
    }
    connect();

    // Poll every 5 s, but not while the tab is hidden; reload right away when the connection comes back
    const pollInterval = setInterval(() => {
      if (mountedRef.current && !document.hidden && fetchRef.current) fetchRef.current({ poll: true });
    }, 5000);
    const onReconnected = () => { if (mountedRef.current && fetchRef.current) fetchRef.current(); };
    window.addEventListener('flux:reconnected', onReconnected);

    return () => {
      mountedRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
      clearInterval(pollInterval);
      window.removeEventListener('flux:reconnected', onReconnected);
    };
  }, [page, pageSize, showMyOnly]);

  // Search / filter changes only: on mount the effect above already loads (this one used to send a
  // second identical request with it)
  const filterMountedRef = useRef(false);
  useEffect(() => {
    if (!filterMountedRef.current) { filterMountedRef.current = true; return; }
    fetchData();
  }, [searchQuery, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // A failed load never empties the list (it used to: one slow / failed request — panel restart,
  // timeout, network blip — showed "No matches found"). The last good list stays; the error is
  // shown only when nothing could be loaded yet. Logo versions come with each match
  // (team1_avatar_v / team2_avatar_v) — the page used to also load the whole teams list for them.
  const fetchData = async ({ poll = false } = {}) => {
    if (poll && inFlightRef.current) return; // the previous load is still running
    const seq = ++seqRef.current;
    inFlightRef.current = true;
    try {
      const myFilter = showMyOnly && userSteam ? `&created_by_steam=${userSteam}` : (showMyOnly && userId ? `&created_by=${userId}` : '');
      // When searching/filtering or pageSize is "all", we fetch the whole set and
      // filter/show client-side (so pagination is a single page). Otherwise we do
      // real server-side pagination for the CURRENT page — previously this always
      // requested page=1, so Next/Back changed the page number but re-fetched the
      // same first page, making pagination look broken.
      const fetchAll = !!searchQuery || filterStatus !== 'all' || pageSize === 'all';
      const limit = fetchAll ? 10000 : pageSize;
      const reqPage = fetchAll ? 1 : page;
      const [matchesRes] = await Promise.allSettled([
        axios.get(`/api/matches?page=${reqPage}&limit=${limit}${myFilter}`),
      ]);
      if (!mountedRef.current || seq !== seqRef.current) return; // a newer load (other search / page) won

      // An answer that isn't a list (e.g. a proxy's HTML error page with status 200) counts as a failed load
      const matchesData = matchesRes.status === 'fulfilled' ? (matchesRes.value.data?.data || matchesRes.value.data) : null;
      if (Array.isArray(matchesData)) {
        setMatches(matchesData);
        if (matchesRes.value.data.pagination) {
          setTotalPages(matchesRes.value.data.pagination.totalPages);
        }
        loadedRef.current = true;
        setFetchError(null);
      } else if (!loadedRef.current) {
        const err = matchesRes.status === 'rejected' ? matchesRes.reason : null;
        setFetchError(err?.response?.data?.error || t('matches.loadFailed', "Couldn't load the matches."));
      }
    } finally {
      if (seq === seqRef.current) inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  };
  fetchRef.current = fetchData;

  const handleCancelMatch = async (matchId) => {
    setConfirmModal({
      show: true,
      message: 'Cancel this match? This will free the server.',
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.put(`/api/matches/${matchId}/status`, { status: 'cancelled' });
          setAlertModal({ show: true, message: 'Match cancelled', type: 'success' });
          fetchData();
        } catch (e) { setAlertModal({ show: true, message: 'Failed to cancel', type: 'error' }); }
      }
    });
  };

  const handleDeleteMatch = async (matchId) => {
    setConfirmModal({
      show: true,
      message: 'Delete this match history permanently?',
      onConfirm: async () => {
        setConfirmModal({ show: false, message: '', onConfirm: null });
        try {
          await axios.delete(`/api/matches/${matchId}`);
          setAlertModal({ show: true, message: 'Match deleted', type: 'success' });
          fetchData();
        } catch (e) { setAlertModal({ show: true, message: 'Failed to delete', type: 'error' }); }
      }
    });
  };

  const filteredMatches = matches.filter(m => {
    if (filterStatus !== 'all' && m.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (m.team1_name || '').toLowerCase().includes(q) || (m.team2_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const getStatusInfo = (m) => {
    const isLive = m.status === 'live';
    const isDone = m.status === 'finished';
    const isCancelled = m.status === 'cancelled';
    const label =
      isLive ? 'LIVE' :
        isDone ? 'FINISHED' :
          isCancelled ? 'CANCELLED' :
            (m.match_stage || 'PENDING').toUpperCase();
    const color =
      isLive ? 'var(--accent-green)' :
        isDone ? 'var(--text-muted)' :
          isCancelled ? 'var(--accent-red)' :
            'var(--accent-amber)';
    return { label, color, isLive, isDone, isCancelled };
  };

  // First load: grey placeholder rows shaped like the list (heading, filter line, day groups)
  if (loading) return (
    <div className="mt-page mt-skel" role="status" aria-busy="true" aria-label={t('matches.loadingList', 'Loading matches...')}>
      <div className="mt-skel-bar title" />
      <div className="mt-skel-line">
        <div className="mt-skel-bar tab" /><div className="mt-skel-bar tab" /><div className="mt-skel-bar tab" /><div className="mt-skel-bar tab" />
        <div className="mt-skel-bar search" />
      </div>
      {[4, 3].map((rows, g) => (
        <div key={g} className="mt-skel-group">
          <div className="mt-skel-bar day" />
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="mt-skel-row">
              <div className="mt-skel-bar time" />
              <div className="mt-skel-bar logo" /><div className="mt-skel-bar name" />
              <div className="mt-skel-bar score" />
              <div className="mt-skel-bar name" /><div className="mt-skel-bar logo" />
              <div className="mt-skel-bar meta" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  // Card view. Render function (NOT an inline component) — called directly in the list so the card
  // reconciles by key across the 5 s poll instead of remounting. The stretched <Link> (z 5) makes
  // the whole card open the match natively (survives re-renders); header actions sit above it (z 10+).
  const renderCard = (m) => {
    const { isLive, isDone, isCancelled } = getStatusInfo(m);
    const statusKey = isLive ? 'live' : isDone ? 'finished' : isCancelled ? 'cancelled' : 'pending';
    const statusText = isLive ? t('matchStatus.live', 'Live') : isDone ? t('matchStatus.finished', 'Finished')
      : isCancelled ? t('matchStatus.cancelled', 'Cancelled') : (m.match_stage ? m.match_stage : t('matchStatus.pending', 'Pending'));
    const mapName = m.map ? m.map.replace(/^(de_|cs_)/, '') : 'TBD';
    const bo = parseInt(m.best_of, 10) || 1;
    const s1 = bo > 1 ? (m.map_wins_team1 ?? 0) : (m.score_team1 ?? 0);
    const s2 = bo > 1 ? (m.map_wins_team2 ?? 0) : (m.score_team2 ?? 0);
    const win1 = isDone && s1 > s2, win2 = isDone && s2 > s1;
    let mapStats = [];
    try { mapStats = typeof m.map_stats === 'string' ? JSON.parse(m.map_stats) : (m.map_stats || []); } catch (_) { }
    if (!Array.isArray(mapStats)) mapStats = [];
    const chips = bo > 1 ? mapStats.filter(Boolean).map((ms, i) => ({ key: i, name: String(ms.map_name || '').replace(/^(de_|cs_)/, ''), a: ms.score_team1, b: ms.score_team2 })) : [];
    if (bo > 1 && isLive) chips.push({ key: 'cur', current: true, name: mapName, a: m.score_team1 ?? 0, b: m.score_team2 ?? 0 });
    const when = m.started_at || m.created_at;

    return (
      <div key={m.id} className={`mcard${isLive ? ' live' : ''}${isCancelled ? ' cancelled' : ''}`}>
        <Link to={`/admin/matches/${m.id}`} className="mcard-link" aria-label={`Open match ${m.id}`} />

        <div className="mcard-head">
          <span className={`mcard-status ${statusKey}`}>{statusText}</span>
          {bo > 1 && <span className="mcard-chip">BO{bo}</span>}
          <span className="mcard-id">#{m.id}</span>
          <div className="mcard-head-right">
            {bo === 1 && <span className="mcard-map">{mapName}</span>}
            {m.bracket_id && (
              <Link to={`/admin/seasons/${m.season_id}/brackets`} onClick={e => e.stopPropagation()} className="mcard-bracket">{t('matches.bracket', 'Bracket')}</Link>
            )}
            {canManage(m) && (
              <MatchActionIcon match={m} onCancel={handleCancelMatch} onDelete={handleDeleteMatch} className="mcard-icon" />
            )}
          </div>
        </div>

        <div className="mcard-body">
          <div className={`mcard-team t1${win2 ? ' lose' : ''}`}>
            <TeamMark className="mcard-logo" id={m.team1_id} avatarV={m.team1_avatar_v} name={m.team1_name} />
            <span className="name" title={m.team1_name}>{m.team1_name}</span>
          </div>
          <div className="mcard-score">
            {m.status === 'pending' ? <span className="vs">VS</span> : (
              <span className={`big${isLive ? ' live' : ''}`}>
                <b className={win1 ? 'w' : win2 ? 'l' : ''}>{s1}</b><i>:</i><b className={win2 ? 'w' : win1 ? 'l' : ''}>{s2}</b>
              </span>
            )}
            {bo > 1 && <span className="sub">{t('matches.series', 'Series')}</span>}
          </div>
          <div className={`mcard-team t2${win1 ? ' lose' : ''}`}>
            <TeamMark className="mcard-logo" id={m.team2_id} avatarV={m.team2_avatar_v} name={m.team2_name} />
            <span className="name" title={m.team2_name}>{m.team2_name}</span>
          </div>
        </div>

        {chips.length > 0 && (
          <div className="mcard-maps">
            {chips.map(c => <span key={c.key} className={`mcard-mapchip${c.current ? ' current' : ''}`}>{c.name || '?'} <b>{c.a ?? 0}-{c.b ?? 0}</b></span>)}
          </div>
        )}

        <div className="mcard-foot">
          {m.server_country_code && (
            <img src={`https://flagcdn.com/16x12/${m.server_country_code.toLowerCase()}.png`} alt={m.server_country_code} width="14" height="10"
              title={`${m.server_city ? m.server_city + ', ' : ''}${m.server_country_name || m.server_country_code}`} onError={e => { e.target.style.display = 'none'; }} />
          )}
          <span className="srv">{m.server_name || t('matches.noServer', 'No server')}</span>
          {when && <span className="when">{formatShortDateTime(when)}</span>}
        </div>
      </div>
    );
  };

  const statusOptions = ['all', 'live', 'finished', 'pending', 'cancelled'];

  return (
    <div className="mt-page">
      {/* -- Header bar -- */}
      <div className="mt-head">
        <h1 className="mt-title">{t('nav.matches', 'Matches')}</h1>
        <button type="button" className="mt-btn primary" onClick={() => navigate('/admin/matches/new')}>
          + {t('nav.createMatch', 'Create Match')}
        </button>
      </div>

      {/* -- Filter bar -- */}
      <div className="mt-bar">
        <div className="mt-seg" role="group" title={showMyOnly ? t('matches.showingMine', 'Showing only your matches') : t('matches.showingAll', 'Showing all matches')}>
          <button type="button" className={showMyOnly ? '' : 'on'} aria-pressed={!showMyOnly} onClick={() => { if (showMyOnly) { setShowMyOnly(false); setPage(1); } }}>
            <IconUsers size={12} /> {t('common.all', 'All')}
          </button>
          <button type="button" className={showMyOnly ? 'on' : ''} aria-pressed={showMyOnly} onClick={() => { if (!showMyOnly) { setShowMyOnly(true); setPage(1); } }}>
            <IconUser size={12} /> {t('matches.mine', 'Mine')}
          </button>
        </div>

        {/* status: underlined tabs */}
        <div className="mt-tabs" role="group">
          {statusOptions.map(s => (
            <button key={s} type="button" className={filterStatus === s ? 'on' : ''} aria-pressed={filterStatus === s} onClick={() => setFilterStatus(s)}>
              {s === 'all' ? t('matches.allStatus', 'All Status') : t(`matchStatus.${s}`, s)}
            </button>
          ))}
        </div>

        <input className="mt-search" placeholder={t('matches.searchTeams', 'Search teams...')} aria-label={t('matches.searchTeams', 'Search teams...')}
          value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />

        <div className="mt-bar-right">
          <div className="mt-seg" role="group" aria-label={t('matches.viewToggle', 'View')}>
            <button type="button" className={viewMode === 'hltv' ? 'on' : ''} aria-pressed={viewMode === 'hltv'} onClick={() => setViewMode('hltv')} title={t('matches.viewHltv', 'HLTV')}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M3 5h18M3 10h18M3 15h18M3 20h18" /></svg>
              {t('matches.viewHltv', 'HLTV')}
            </button>
            <button type="button" className={viewMode === 'cards' ? 'on' : ''} aria-pressed={viewMode === 'cards'} onClick={() => setViewMode('cards')} title={t('matches.viewCards', 'Cards')}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></svg>
              {t('matches.viewCards', 'Cards')}
            </button>
          </div>
          <span className="mt-count">
            {t('matches.matchCount', 'Matches: {{n}}', { n: filteredMatches.length })}
          </span>
          <div className="mt-seg" role="group" aria-label={t('matches.perPage', 'Matches per page')}>
            {[10, 50, 'all'].map(size => (
              <button key={size} type="button" className={pageSize === size ? 'on' : ''} aria-pressed={pageSize === size} onClick={() => { setPageSize(size); setPage(1); }}>
                {size === 'all' ? t('common.all', 'All') : size}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* -- Match list: HLTV-style rows, or the cards grid (a failed first load shows the error, not "No matches found") -- */}
      {fetchError && matches.length === 0 ? (
        <div className="mt-error" role="alert">
          <span>{fetchError}</span>
          <button type="button" className="mt-btn" onClick={() => { setLoading(true); fetchData(); }}>
            {t('common.retry', 'Retry')}
          </button>
        </div>
      ) : viewMode === 'hltv' ? (
        <MatchListHltv
          matches={filteredMatches}
          canManage={canManage}
          onCancel={handleCancelMatch}
          onDelete={handleDeleteMatch}
        />
      ) : (
      <div className="mc-grid">
        {filteredMatches.length > 0 ? (
          filteredMatches.map(m => renderCard(m))
        ) : (
          <div className="mc-empty">{t('matches.noMatchesFound', 'No matches found')}</div>
        )}
      </div>
      )}

      {/* Pagination */}
      <div className="mt-pager">
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {/* Responsive style injection */}
      <style>{`
        @media (max-width: 768px) {
          div[style*="grid-template-columns: repeat(2"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <AlertModal show={alertModal.show} message={alertModal.message} type={alertModal.type} onClose={() => setAlertModal({ ...alertModal, show: false })} />
      <ConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal({ ...confirmModal, show: false })} />
    </div>
  );
}

export default Matches;
